import React, { useState } from "react";
import Shell from "./CopilotCardShell";

/* How the proposal gets written — the fork that comes straight after setup.
 *
 * This decides whether there is an outline at all: section-by-section runs the
 * full pipeline (requirements → outline → approval → drafting), one-go skips
 * all of it and writes the document in a single pass.
 *
 * It replaces the old post-outline mode card, which offered "section by section"
 * against an "all at once" that was never implemented. That card asked the
 * question in the wrong place — after the outline had already been built, which
 * is most of the work one-go exists to skip.
 *
 * WHY TWO STACKED OPTIONS AND NOT A SEGMENTED PILL. A pill is built for two
 * equivalent halves. These two are not equivalent — one is the thorough route
 * and one is the fast one, and each needs a line of explanation, which a pill
 * has nowhere to put. Stacked options give each its reason.
 *
 * The trade is stated on the card rather than discovered afterwards: one-go
 * does not extract requirements, so the Requirements tab and go/no-go stay
 * empty for that project. Finding that out later feels like a bug.
 */

const OPTIONS = [
  {
    id: "section",
    label: "Section by section",
    line: "I plan the outline, you approve it, then each section is written and shown to you.",
    note: "Requirements are extracted and mapped, so coverage is checkable.",
  },
  {
    id: "oneGo",
    label: "In one go",
    line: "I read the whole RFP and write the complete draft in one pass.",
    note: "Much faster. No outline to approve, and no requirements checklist.",
  },
];

const ProposalApproachCard = ({ message, onChoose }) => {
  const [choice, setChoice] = useState("section");
  const [started, setStarted] = useState(false);

  const start = () => {
    if (started) return;
    setStarted(true);
    onChoose?.({ approach: choice, ...message });
  };

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 3v12M6 21v-2M18 3v2M18 9v12" />
          <circle cx="6" cy="18" r="2.4" />
          <circle cx="18" cy="7" r="2.4" />
        </svg>
      }
      title="How should I write it?"
      width={640}
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={start}
            disabled={started}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-4 py-2 text-[13.5px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {started ? "Starting…" : "Start"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 px-4 py-3.5" role="radiogroup" aria-label="Writing approach">
        {OPTIONS.map((o) => {
          const on = choice === o.id;
          return (
            <button
              key={o.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={started}
              onClick={() => setChoice(o.id)}
              className={`w-full rounded-xl border px-3.5 py-3 text-left transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                on
                  ? "border-[#A04A97] bg-[#A04A97]/[0.05]"
                  : "border-gray-200 bg-white hover:border-[#A04A97]/40"
              }`}
            >
              <span className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className={`mt-[3px] flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full border transition-colors duration-200 ${
                    on ? "border-[#A04A97]" : "border-gray-300"
                  }`}
                >
                  {on && <span className="h-1.5 w-1.5 rounded-full bg-[#A04A97]" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-semibold text-[#18101C]">{o.label}</span>
                  <span className="mt-0.5 block text-[12.5px] leading-relaxed text-[#52525B]">{o.line}</span>
                  <span className="mt-1 block text-[12px] leading-relaxed text-[#77707E]">{o.note}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </Shell>
  );
};

export default ProposalApproachCard;
